import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Loader2, CheckCircle2, XCircle, HandCoins, Clock, ArrowLeft, RefreshCw, User, MapPin, Calendar } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  counter_offered: "bg-sky-100 text-sky-800",
  accepted: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
  expired: "bg-gray-200 text-gray-600",
};

export default function Offer() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [acting, setActing] = useState(null);
  const [counterPrice, setCounterPrice] = useState("");
  const [counterMsg, setCounterMsg] = useState("");
  const [showCounter, setShowCounter] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/offers/${offerId}`);
      setOffer(data);
      setCounterPrice(((data.offered_price || 0) + 5).toFixed(2));
    } catch {
      setOffer(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [offerId]);

  const act = async (action) => {
    if (acting) return;
    setActing(action);
    try {
      if (action === "accept") {
        const { data } = await api.post(`/offers/${offerId}/accept`, {});
        setOffer(data.offer);
        if (data.booking) toast.success("Offer accepted — your booking is confirmed!");
        else toast.success("Offer accepted. We'll be in touch shortly.");
      } else if (action === "reject") {
        const { data } = await api.post(`/offers/${offerId}/reject`, { message: counterMsg || null });
        setOffer(data);
        toast.success("Offer closed. Feel free to come back with a new number.");
      } else if (action === "counter") {
        const price = parseFloat(counterPrice);
        if (!price || price <= 0) { setActing(null); return toast.error("Enter a valid counter price"); }
        const { data } = await api.post(`/offers/${offerId}/counter-customer`, { price, message: counterMsg || null });
        setOffer(data);
        setShowCounter(false);
        toast.success(`Counter of $${price.toFixed(2)} sent.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setActing(null);
    }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#047857]" /></div>;
  if (!offer) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-extrabold tracking-tighter font-[Outfit] text-gray-900">Offer not found</h1>
        <Button onClick={() => navigate("/")} className="mt-6 rounded-full">Go home</Button>
      </div>
    );
  }

  const isFinal = ["accepted", "rejected", "expired"].includes(offer.status);
  const lastByAdmin = offer.last_action_by === "admin";
  const showCustomerActions = !isFinal && lastByAdmin; // it's the customer's turn

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="offer-page">
      <button onClick={() => navigate("/")} className="inline-flex items-center text-sm text-gray-500 hover:text-[#047857] mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#064E3B] to-[#047857] text-white rounded-3xl p-7 shadow-xl">
        <div className="flex items-center gap-2">
          <HandCoins className="w-5 h-5 text-emerald-200" />
          <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-100">JDSN · Negotiation</div>
        </div>
        <h1 className="mt-1 text-2xl font-extrabold font-[Outfit] tracking-tighter">{offer.customer_name}</h1>
        {offer.address && <p className="text-emerald-100 text-sm flex items-center gap-1.5 mt-0.5"><MapPin className="w-3.5 h-3.5" /> {offer.address}</p>}

        <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-200 font-bold">Current ask</div>
            <div className="text-5xl font-extrabold tracking-tighter tabular-nums font-[Outfit]">${(offer.offered_price || 0).toFixed(2)}</div>
            <div className="text-xs text-emerald-100 mt-1">Estimated: ${(offer.estimated_price || 0).toFixed(2)}</div>
          </div>
          <div className="text-right">
            <span className={`text-[10px] uppercase tracking-[0.16em] font-extrabold px-3 py-1.5 rounded-full ${STATUS_STYLES[offer.status] || "bg-white/15 text-white"}`} data-testid="offer-status">
              {offer.status.replace(/_/g, " ")}
            </span>
            {offer.expires_at && !isFinal && (
              <div className="mt-2 text-xs text-emerald-100 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Until {offer.expires_at.split("T")[0]}</div>
            )}
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="mt-6 bg-white rounded-3xl border border-gray-200 p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Job details</div>
        <div className="mt-2 grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
          <div><span className="text-gray-500">Service:</span> <strong>{offer.service_name || offer.service_key || "—"}</strong></div>
          <div><span className="text-gray-500">Square ft:</span> <strong>{offer.square_feet || "—"}</strong></div>
          <div><span className="text-gray-500">Frequency:</span> <strong className="capitalize">{(offer.frequency || "").replace(/_/g, " ")}</strong></div>
          <div><span className="text-gray-500">Yard area:</span> <strong className="capitalize">{offer.yard_area}</strong></div>
          {offer.preferred_date && <div className="col-span-2 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#047857]" /> <strong>{offer.preferred_date} {offer.preferred_time}</strong></div>}
        </div>
        {offer.notes && (
          <div className="mt-3 bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-sm text-gray-800 whitespace-pre-wrap">
            <strong>Note:</strong> {offer.notes}
          </div>
        )}
      </div>

      {/* History thread */}
      <div className="mt-6 bg-white rounded-3xl border border-gray-200 p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500 mb-3">Negotiation history</div>
        <ul className="space-y-3">
          {(offer.history || []).map((h, i) => (
            <li key={i} className={`flex gap-3 items-start ${h.by === "customer" ? "" : "flex-row-reverse text-right"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${h.by === "customer" ? "bg-gray-100 text-gray-700" : h.by === "admin" ? "bg-[#047857] text-white" : "bg-gray-200 text-gray-500"}`}>
                {h.by === "customer" ? <User className="w-4 h-4" /> : h.by === "admin" ? "J" : <RefreshCw className="w-3.5 h-3.5" />}
              </div>
              <div className={`flex-1 max-w-[78%] ${h.by === "customer" ? "" : "ml-auto"}`}>
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500">{h.by} · {h.action} {h.at && `· ${h.at.split("T")[1]?.slice(0,5)}`}</div>
                {h.price !== undefined && h.price !== null && <div className="font-extrabold text-[#047857] tabular-nums">${Number(h.price).toFixed(2)}</div>}
                {h.message && <div className="text-sm text-gray-700 mt-0.5">&ldquo;{h.message}&rdquo;</div>}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Customer actions */}
      {!isFinal && (
        <div className="mt-6 bg-white rounded-3xl border border-gray-200 p-6">
          {showCustomerActions ? (
            <>
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500 mb-3">JDSN just countered — your move</div>
              <div className="grid sm:grid-cols-3 gap-2">
                <Button onClick={() => act("accept")} disabled={!!acting} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold py-6" data-testid="offer-accept">
                  {acting === "accept" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Accept ${(offer.offered_price || 0).toFixed(2)}</>}
                </Button>
                <Button variant="outline" onClick={() => setShowCounter((s) => !s)} disabled={!!acting} className="rounded-full font-bold py-6" data-testid="offer-counter-toggle">
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Counter back
                </Button>
                <Button variant="outline" onClick={() => act("reject")} disabled={!!acting} className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50 font-bold py-6" data-testid="offer-reject">
                  <XCircle className="w-4 h-4 mr-1.5" /> Decline
                </Button>
              </div>
              {showCounter && (
                <div className="mt-4 space-y-2 border-t border-gray-100 pt-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-extrabold text-gray-400">$</span>
                    <Input type="number" min="1" step="0.01" value={counterPrice} onChange={(e) => setCounterPrice(e.target.value)} className="text-2xl font-extrabold h-14 tabular-nums" data-testid="offer-counter-price" />
                  </div>
                  <Textarea rows={2} value={counterMsg} onChange={(e) => setCounterMsg(e.target.value)} placeholder="Optional message" />
                  <Button onClick={() => act("counter")} disabled={!!acting} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-bold" data-testid="offer-counter-submit">
                    {acting === "counter" ? <Loader2 className="w-4 h-4 animate-spin" /> : `Send counter of $${parseFloat(counterPrice || 0).toFixed(2)}`}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-sm text-gray-500">
              Offer submitted. We&apos;ll respond within 24 hours &mdash; you&apos;ll get an SMS/email.
            </div>
          )}
        </div>
      )}

      {isFinal && (
        <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-3xl p-6 text-center">
          {offer.status === "accepted" && (<><CheckCircle2 className="w-10 h-10 mx-auto text-[#047857]" /><div className="mt-2 text-xl font-extrabold text-[#064E3B]">Deal! ${(offer.offered_price || 0).toFixed(2)}</div>{offer.booking_id && <div className="text-sm text-gray-700 mt-1">Booking #{offer.booking_id.slice(-6)} confirmed for {offer.preferred_date}.</div>}</>)}
          {offer.status === "rejected" && (<><XCircle className="w-10 h-10 mx-auto text-rose-500" /><div className="mt-2 text-xl font-extrabold text-gray-900">Offer closed</div></>)}
          {offer.status === "expired" && (<><Clock className="w-10 h-10 mx-auto text-gray-400" /><div className="mt-2 text-xl font-extrabold text-gray-900">Offer expired</div></>)}
        </div>
      )}
    </div>
  );
}
